import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import toInteger from 'lodash/toInteger';

import {
    SuiContainer,
    SuiButton,
    SuiMenuButton,
    ORIGIN_BOTTOM_LEFT,
    ORIGIN_AUTO_VERTICAL,
    SIZE_NONE,
    SIZE_FULL,
} from '@splashthat/sui';

import { getTicketTypes } from 'common/entities/ticketType/selectors';
import { isFormTypeTicketSale } from 'common/entities/form/selectors';
import {
    getFormTabHideAddQuestionButton,
    getIsUserUsingRoleBasedPermissions,
} from 'common/store/userProperties/selectors';
import { getCanUserCreateRegistrationForm, getCanUserCreateTicketsTab } from 'common/store/permissions';
import { getCanUserUpdateLibraryFormsUserPermission } from 'common/store/userPermissions/selectors';
import PermissionCheckWrapper from 'utils/PermissionCheckWrapper';
import { getEditTicketTypeId, toggleCopyFormModal } from 'entry/workspace/compounds/FormBuilderTicketSettings/modules';

import { getFormId } from '../../selectors';

import { createTextElementContentNode } from '../../actions/nodeActions';
import { openErrorMessageModal } from '../../actions/errorMessage';

export const COPY_QUESTION_FROM_OTHER_TICKET_FORM = 'Copy From Another Ticket';
export const ADD_QUESTION_LABEL = 'Add Question';
const TEXT_ELEMENT_TITLE = 'Add text element';
const EDIT_ERROR_MESSAGES_LABEL = 'Edit custom errors';
const BASE_CLASS_NAME = 'FormEditorLayoutFooter';

class FormEditorLayoutFooter extends Component {
    static propTypes = {
        onClickAddQuestion: PropTypes.func.isRequired,
        hasAddQuestionButton: PropTypes.bool.isRequired,
        createTextElementContentNode: PropTypes.func.isRequired,
        toggleCopyFormModal: PropTypes.func.isRequired,
        isTicketSaleForm: PropTypes.bool.isRequired,
        ticketTypes: PropTypes.array.isRequired,
        openErrorMessageModal: PropTypes.func.isRequired,
        hideAddQuestionButton: PropTypes.bool.isRequired,
        permissions: PropTypes.shape({
            canUserCreateRegistrationForm: PropTypes.bool,
            canUserCreateTicketsTab: PropTypes.bool,
        }).isRequired,
    };

    state = {
        inputEl: null,
    };

    handleCreateTextElement = (ev) => {
        this.props.createTextElementContentNode();
    };

    inputCb = (a) => {
        this.setState({ inputEl: a });
    };

    render() {
        const { isTicketSaleForm, hasAddQuestionButton, ticketTypes, hideAddQuestionButton, permissions } = this.props;
        return (
            <SuiContainer variation="flex-no-shrink flex-horizontal-centered">
                {isTicketSaleForm && (
                    <SuiContainer variation="flex-group">
                        <SuiButton
                            fullWidth
                            size="small"
                            variation="secondary"
                            disabled={!ticketTypes.length}
                            label={COPY_QUESTION_FROM_OTHER_TICKET_FORM}
                            onClick={this.props.toggleCopyFormModal}
                            data-qa={`${BASE_CLASS_NAME}__copy-questions-button`}
                        />
                    </SuiContainer>
                )}
                {isTicketSaleForm && hasAddQuestionButton && (
                    <SuiContainer variation="flex-group">
                        <PermissionCheckWrapper permission={permissions.canUserCreateTicketsTab}>
                            {(restProps) => (
                                <SuiButton
                                    fullWidth
                                    size="small"
                                    label={ADD_QUESTION_LABEL}
                                    onClick={this.props.onClickAddQuestion}
                                    data-qa={`${BASE_CLASS_NAME}__form-lib-button`}
                                    data-pendo="FORM_EDITOR_ADD_QUESTION" // We need to keep this for E2E
                                    disabled={!permissions.canUserCreateTicketsTab}
                                    {...restProps}
                                />
                            )}
                        </PermissionCheckWrapper>
                    </SuiContainer>
                )}

                {!isTicketSaleForm && hasAddQuestionButton && !hideAddQuestionButton && (
                    <SuiContainer variation="flex-group">
                        <div ref={this.inputCb}>
                            <PermissionCheckWrapper
                                permission={
                                    permissions.isUsingRoleBasedPermissions &&
                                    (permissions.canUserCreateRegistrationForm || permissions.canUserEditLibraryForms)
                                }
                            >
                                {(restProps) => (
                                    <SuiMenuButton
                                        {...restProps}
                                        label={ADD_QUESTION_LABEL}
                                        variation={'primary'}
                                        fullWidth
                                        size="small"
                                        data-qa={`${BASE_CLASS_NAME}__menu-button`}
                                        onClick={this.props.onClickAddQuestion}
                                        disabled={
                                            permissions.isUsingRoleBasedPermissions &&
                                            !permissions.canUserCreateRegistrationForm &&
                                            !permissions.canUserEditLibraryForms
                                        }
                                        popoverProps={{
                                            anchorEl: this.state.inputEl,
                                            anchorOrigin: ORIGIN_BOTTOM_LEFT,
                                            targetOrigin: ORIGIN_AUTO_VERTICAL,
                                            targetWidth: SIZE_FULL,
                                            targetHeight: SIZE_NONE,
                                        }}
                                        menuOptions={[
                                            {
                                                label: TEXT_ELEMENT_TITLE,
                                                onClick: this.handleCreateTextElement,
                                            },
                                            {
                                                label: EDIT_ERROR_MESSAGES_LABEL,
                                                onClick: () => {
                                                    this.props.openErrorMessageModal();
                                                },
                                            },
                                        ]}
                                    />
                                )}
                            </PermissionCheckWrapper>
                        </div>
                    </SuiContainer>
                )}
            </SuiContainer>
        );
    }
}

const mapStateToProps = (state) => {
    const editTicketTypeId = getEditTicketTypeId(state);
    const allTicketTypes = getTicketTypes(state);
    const ticketTypes = allTicketTypes.filter((ticketType) => toInteger(ticketType.id) !== toInteger(editTicketTypeId));
    const formId = getFormId(state);
    const isTicketSaleForm = isFormTypeTicketSale(state, formId);
    const hideAddQuestionButton = getFormTabHideAddQuestionButton(state);
    const isUsingRoleBasedPermissions =
        getIsUserUsingRoleBasedPermissions(state) ||
        get(window, 'splash.server.constants.permissions.is_using_role_based_permissions', false);
    const canUserCreateRegistrationForm = getCanUserCreateRegistrationForm(state);
    const canUserCreateTicketsTabStore = getCanUserCreateTicketsTab(state);
    const canUserEditLibraryForms = getCanUserUpdateLibraryFormsUserPermission(state);
    const canUserCreateTicketsTabWindow = get(window, 'splash.server.constants.permissions.create_tickets_tab', false);
    const canUserCreateTicketsTab =
        canUserCreateTicketsTabStore !== undefined ? canUserCreateTicketsTabStore : canUserCreateTicketsTabWindow;

    return {
        ticketTypes,
        isTicketSaleForm,
        hideAddQuestionButton,
        permissions: {
            isUsingRoleBasedPermissions,
            canUserCreateRegistrationForm,
            canUserCreateTicketsTab,
            canUserEditLibraryForms,
        },
    };
};

const mapDispatchToProps = {
    createTextElementContentNode,
    toggleCopyFormModal,
    openErrorMessageModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormEditorLayoutFooter);
